import { BasicDetails } from './basic-details.tsx';
import { ConfirmEmail } from './confirm-email.tsx';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';

// Minimum 8 characters, at least one uppercase letter, one lowercase letter, one number and one special character
const passwordValidation = new RegExp(/^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9]).{8,}$/);

export const getBasicDetailsSchema = (translation?: {
  firstNameRequired: string;
  firstNameLength: string;
  lastNameRequired: string;
  lastNameLength: string;
  emailRequired: string;
  emailValid: string;
  passwordRequired: string;
  passwordComplexity: string;
}) => {
  const messages = translation ?? {
    firstNameRequired: 'First name is required',
    firstNameLength: 'First name must be at least 3 characters',
    lastNameRequired: 'Last name is required',
    lastNameLength: 'Last name must be at least 3 characters',
    emailRequired: 'Email is required',
    emailValid: 'Please enter a valid email',
    passwordRequired: 'Password is required',
    passwordComplexity:
      'Password must contain at least 8 characters, one uppercase letter, one lowercase letter, and one number',
  };

  return z.object({
    firstName: z
      .string({ error: messages.firstNameRequired })
      .min(3, messages.firstNameLength),
    lastName: z.string({ error: messages.lastNameRequired }).min(3, messages.lastNameLength),
    email: z
      .string({ error: messages.emailRequired })
      .min(1, { message: messages.emailRequired })
      .email({ message: messages.emailValid }),
    password: z
      .string({ error: messages.passwordRequired })
      .regex(passwordValidation, { message: messages.passwordComplexity }),
  });
};

export const getConfirmEmailSchema = (translation?: {
  codeRequired: string;
  codeLength: string;
  codeNumber: string;
  codePositive: string;
  emailRequired: string;
  emailValid: string;
}) => {
  const messages = translation ?? {
    codeRequired: 'Verification code is required',
    codeLength: 'Please enter a valid code',
    codeNumber: 'Code must be a number',
    codePositive: 'Code must be a positive number',
    emailRequired: 'Email is required',
    emailValid: 'Please enter a valid email',
  };

  return z.object({
    code: z
      .string({ error: messages.codeRequired })
      .min(1, { message: messages.codeLength })
      .refine((val) => !isNaN(Number(val)), messages.codeNumber)
      .refine((val) => Number(val) >= 0, messages.codePositive),
    email: z
      .string({ error: messages.emailRequired })
      .min(1, { message: messages.emailRequired })
      .email({ message: messages.emailValid }),
  });
};

export const useRegisterSchema = () => {
  const { t } = useTranslation();

  const BasicDetailsSchema = getBasicDetailsSchema({
    firstNameRequired: t('auth.errors.firstNameRequired'),
    firstNameLength: t('auth.errors.firstNameLength'),
    lastNameRequired: t('auth.errors.lastNameRequired'),
    lastNameLength: t('auth.errors.lastNameLength'),
    emailRequired: t('auth.errors.emailRequired'),
    emailValid: t('auth.errors.emailValid'),
    passwordRequired: t('auth.errors.passwordRequired'),
    passwordComplexity: t('auth.errors.passwordComplexity'),
  });

  const ConfirmEmailSchema = getConfirmEmailSchema({
    codeRequired: t('auth.errors.codeRequired'),
    codeLength: t('auth.errors.codeLength'),
    codeNumber: t('auth.errors.codeNumber'),
    codePositive: t('auth.errors.codePositive'),
    emailRequired: t('auth.errors.emailRequired'),
    emailValid: t('auth.errors.emailValid'),
  });

  const registerSchema = z.object({
    basicDetails: BasicDetailsSchema,
    confirmEmail: ConfirmEmailSchema,
  });

  return {
    BasicDetailsSchema,
    ConfirmEmailSchema,
    registerSchema,
  };
};

export const getRegisterSchema = (translation?: {
  firstNameRequired: string;
  firstNameLength: string;
  lastNameRequired: string;
  lastNameLength: string;
  emailRequired: string;
  emailValid: string;
  passwordRequired: string;
  passwordComplexity: string;
  codeRequired: string;
  codeLength: string;
  codeNumber: string;
  codePositive: string;
}) => {
  const BasicDetailsSchema = getBasicDetailsSchema(translation);
  const ConfirmEmailSchema = getConfirmEmailSchema(translation);

  return z.object({
    basicDetails: BasicDetailsSchema,
    confirmEmail: ConfirmEmailSchema,
  });
};

export type TRegisterFormSchema = z.infer<
  ReturnType<typeof useRegisterSchema>['registerSchema']
>;

export const STEPS = [
  {
    number: 1,
    title: 'Basic Details',
    Element: BasicDetails,
    schema: 'basicDetails',
  },
  {
    number: 2,
    title: 'Confirm Email',
    Element: ConfirmEmail,
    schema: 'confirmEmail',
  },
] as const;
